import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { translateManyToArabic } from '../utils/translationApi';

type Language = 'en' | 'ar';
type TranslationCache = Record<string, string>;
type AttributeMutation = {
  element: Element;
  attr: string;
  original: string;
};

interface LanguageContextType {
  language: Language;
  isArabic: boolean;
  toggleLanguage: () => void;
  t: (key: string) => string;
  t_api: (text: string, arText?: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const englishTranslations: Record<string, string> = {
  'nav.home': 'Home',
  'nav.about': 'About Us',
  'nav.group': 'Our Group',
  'nav.news': 'News',
  'nav.careers': 'Careers',
  'nav.contact': 'Contact Us',
  'lang.switch': 'Arabic',
  'nav.oman': 'Oman',
  'nav.qatar': 'Qatar',
  'nav.uae': 'UAE',
  'nav.group_1': 'Hamdan Construction',
  'nav.group_2': 'Hamdan Exchange',
  'nav.group_3': 'Hamdan International for Gabbro',
  'nav.group_4': 'Hamdan Mining',
  'nav.group_5': 'Hamdan Plaza Hotel',
  'nav.group_6': 'Hamdan Real Estates',
  'nav.group_6_sub': 'Al Yasminah Real Estate',
  'nav.group_7': 'Hamdan Tires, Battery & Accessories',
  'nav.group_8': 'Hamdan Tour And Travel',
  'nav.group_9': 'Hamdan Transport Division',
  'footer.quick_links': 'Quick Links',
  'footer.newsletter': 'Newsletter',
  'footer.subscribe': 'Subscribe',
  'footer.email_placeholder': 'Your email address',
  'footer.all_rights': 'All rights reserved.',
  'footer.developed_by': 'Developed and Maintained by',
  'about.founder': "Founder's Message",
  'about.vision': 'Vision & Mission',
  'about.news': 'News',
  'about.group': 'Hamdan Group',
  'common.projects': 'Projects',
  'common.branches': 'Branches',
  'common.gallery': 'Gallery',
  'common.operations': 'Operations',
  'common.clients': 'Clients',
  'common.promotions': 'Promotions',
  'common.services': 'Services',
  'common.why_us': 'Why Choose Us',
  'common.albaz_center': 'Al Baz Center',
  'common.hamdan_complex': 'Hamdan Complex',
  'common.qa': 'Quality Assurance',
  'common.intro': 'Introduction',
  'common.history': 'History',
  'common.qhse': 'QHSE',
  'common.tech': 'Innovation & Technology',
  'common.awards': 'Awards',
  'common.about': 'About Us',
  'common.contact': 'Contact',
  'common.read_more': 'Read More',
  'common.learn_more': 'Learn More',
  'common.loading': 'Loading...',
  'common.apply_now': 'Apply Now',
  'careers.title': 'Your career at Hamdan',
  'careers.vacancies': 'Active Vacancies',
  'careers.search_placeholder': 'e.g. Engineer',
  'careers.level': 'Career Level',
  'careers.all_levels': 'All Levels',
  'careers.skills': 'Skills Required',
  'careers.search_button': 'Search Jobs',
  'careers.loading_jobs': 'Loading open positions...',
  'careers.no_jobs': 'No jobs match your search',
  'careers.landing.hero_subtitle': 'Take your future in your hands.',
  'careers.landing.apply_now': 'Apply Now',
  'careers.landing.culture': 'Our Culture',
  'careers.landing.join_us': 'Join us in a place where you can truly be yourself and belong.',
  'careers.landing.nationalities': 'Nationalities',
  'careers.landing.years': 'Years Legend',
  'careers.landing.benefits': 'Benefits',
  'careers.landing.why_join': 'Why join Hamdan Trading Group?',
  'careers.landing.shape_future': 'Shape Your Future',
  'careers.landing.take_lead': 'Take The Lead',
  'careers.landing.be_yourself': 'Be Yourself',
  'careers.landing.make_mark': 'Make Your Mark',
  'careers.landing.view_apply': 'View & Apply',
  'careers.landing.view_all': 'View All Jobs',
  'careers.landing.onboarding': 'Onboarding',
  'careers.landing.getting_started': 'GETTING STARTED WITH US',
  'careers.landing.internship': 'Internship Programme',
  'careers.landing.professionals': 'Experienced Professionals',
  'careers.landing.feedback': 'Feedback',
  'careers.landing.what_employees_say': "HERE'S WHAT OUR EMPLOYEES SAY",
  'careers.clear_filters': 'Clear Filters',
  'contact.office_contacts': 'Office Contacts',
  'contact.salalah': 'Salalah',
  'contact.muscat': 'Muscat',
  'contact.telephone': 'Telephone',
  'contact.fax': 'Fax',
  'contact.send_mail': 'Send Us Mail',
  'contact.name': 'Your Name',
  'contact.email': 'Your Email',
  'contact.phone': 'Your Contact',
  'contact.subject': 'Subject',
  'contact.message': 'Your Message',
  'contact.send': 'SEND MESSAGE',
  'contact.info': 'Contact info',
  'contact.support': 'Support info',
  'contact.address': 'Address',
  'contact.area': 'Area',
  'legal.privacy': 'Privacy Notice',
  'legal.cookie': 'Cookie Policy',
  'legal.terms': 'Terms & Conditions',
  'legal.statement': 'Legal Statement',
  'legal.title': 'Legal',
  'legal.last_updated': 'Last Updated',
  'common.our_services': 'Our Services',
  'common.operations_overview': 'Operations Overview',
  'common.click_to_view': 'Click to view',
  'common.selected_section': 'Selected Section',
  'common.product_portfolio': 'Product Portfolio (Technical Specifications)',
  'common.select_category_filter': 'Select a category to filter products.',
  'common.catalog': 'Catalog',
  'common.all_products': 'All Products',
  'common.items_listed': 'Items Listed',
  'common.no_products_found': 'No Products Found',
  'common.try_selecting_category': 'Try selecting another category or view all products.',
  'common.sincerely': 'Sincerely,',
  'common.get_in_touch': 'Get in Touch',
};

const arabicTranslations: Record<string, string> = {
  'nav.home': 'الرئيسية',
  'nav.about': 'من نحن',
  'nav.group': 'مجموعة حمدان',
  'nav.news': 'الأخبار',
  'nav.careers': 'الوظائف',
  'nav.contact': 'تواصل معنا',
  'lang.switch': 'English',
  'nav.oman': 'عمان',
  'nav.qatar': 'قطر',
  'nav.uae': 'الإمارات',
  'nav.group_1': 'حمدان للإنشاءات',
  'nav.group_2': 'حمدان للصرافة',
  'nav.group_3': 'حمدان الدولية للجابرو',
  'nav.group_4': 'حمدان للتعدين',
  'nav.group_5': 'فندق حمدان بلازا',
  'nav.group_6': 'حمدان للعقارات',
  'nav.group_6_sub': 'الياسمينة للعقارات',
  'nav.group_7': 'حمدان للإطارات والبطاريات والملحقات',
  'nav.group_8': 'حمدان للسياحة والسفر',
  'nav.group_9': 'قسم حمدان للنقل',
  'footer.quick_links': 'روابط سريعة',
  'footer.newsletter': 'النشرة الإخبارية',
  'footer.subscribe': 'اشترك',
  'footer.email_placeholder': 'عنوان بريدك الإلكتروني',
  'footer.all_rights': 'جميع الحقوق محفوظة.',
  'footer.developed_by': 'تم التطوير والصيانة بواسطة',
  'about.founder': 'رسالة المؤسس',
  'about.vision': 'الرؤية والرسالة',
  'about.news': 'الأخبار',
  'about.group': 'مجموعة حمدان',
  'common.projects': 'المشاريع',
  'common.branches': 'الفروع',
  'common.gallery': 'المعرض',
  'common.operations': 'العمليات',
  'common.clients': 'العملاء',
  'common.promotions': 'العروض',
  'common.services': 'الخدمات',
  'common.why_us': 'لماذا تختارنا',
  'common.albaz_center': 'مركز الباز',
  'common.hamdan_complex': 'مجمع حمدان',
  'common.qa': 'ضمان الجودة',
  'common.intro': 'مقدمة',
  'common.history': 'التاريخ',
  'common.qhse': 'الجودة والصحة والسلامة والبيئة',
  'common.tech': 'الابتكار والتكنولوجيا',
  'common.awards': 'الجوائز',
  'common.about': 'من نحن',
  'common.contact': 'تواصل معنا',
  'common.read_more': 'اقرأ المزيد',
  'common.learn_more': 'اعرف المزيد',
  'common.loading': 'جار التحميل...',
  'common.apply_now': 'قدم الآن',
  'careers.title': 'مسيرتك المهنية في حمدان',
  'careers.vacancies': 'الوظائف الشاغرة',
  'careers.search_placeholder': 'مثال: مهندس',
  'careers.level': 'المستوى الوظيفي',
  'careers.all_levels': 'جميع المستويات',
  'careers.skills': 'المهارات المطلوبة',
  'careers.search_button': 'البحث عن وظائف',
  'careers.loading_jobs': 'جار تحميل الوظائف المتاحة...',
  'careers.no_jobs': 'لا توجد وظائف تطابق بحثك',
  'careers.landing.hero_subtitle': 'خذ مستقبلك بين يديك.',
  'careers.landing.apply_now': 'قدم الآن',
  'careers.landing.culture': 'ثقافتنا',
  'careers.landing.join_us': 'انضم إلينا في مكان يمكنك أن تكون فيه على طبيعتك وتنتمي إليه.',
  'careers.landing.nationalities': 'الجنسيات',
  'careers.landing.years': 'سنوات الخبرة',
  'careers.landing.benefits': 'المزايا',
  'careers.landing.why_join': 'لماذا تنضم إلى مجموعة حمدان التجارية؟',
  'careers.landing.shape_future': 'اصنع مستقبلك',
  'careers.landing.take_lead': 'تولى القيادة',
  'careers.landing.be_yourself': 'كن على طبيعتك',
  'careers.landing.make_mark': 'اترك بصمتك',
  'careers.landing.view_apply': 'عرض وتقديم',
  'careers.landing.view_all': 'عرض كل الوظائف',
  'careers.landing.onboarding': 'الانضمام',
  'careers.landing.getting_started': 'ابدأ معنا',
  'careers.landing.internship': 'برنامج التدريب',
  'careers.landing.professionals': 'أصحاب الخبرة',
  'careers.landing.feedback': 'آراء الموظفين',
  'careers.landing.what_employees_say': 'ما يقوله موظفونا',
  'careers.clear_filters': 'مسح الفلاتر',
  'contact.office_contacts': 'جهات اتصال المكتب',
  'contact.salalah': 'صلالة',
  'contact.muscat': 'مسقط',
  'contact.telephone': 'الهاتف',
  'contact.fax': 'الفاكس',
  'contact.send_mail': 'أرسل لنا رسالة',
  'contact.name': 'اسمك',
  'contact.email': 'بريدك الإلكتروني',
  'contact.phone': 'رقم التواصل',
  'contact.subject': 'الموضوع',
  'contact.message': 'رسالتك',
  'contact.send': 'إرسال الرسالة',
  'contact.info': 'معلومات الاتصال',
  'contact.support': 'معلومات الدعم',
  'contact.address': 'العنوان',
  'contact.area': 'المنطقة',
  'legal.privacy': 'إشعار الخصوصية',
  'legal.cookie': 'سياسة ملفات تعريف الارتباط',
  'legal.terms': 'الشروط والأحكام',
  'legal.statement': 'البيان القانوني',
  'legal.title': 'قانوني',
  'legal.last_updated': 'آخر تحديث',
  'common.our_services': 'خدماتنا',
  'common.operations_overview': 'نظرة عامة على العمليات',
  'common.click_to_view': 'انقر للعرض',
  'common.selected_section': 'القسم المحدد',
  'common.product_portfolio': 'محفظة المنتجات (المواصفات الفنية)',
  'common.select_category_filter': 'حدد فئة لتصفية المنتجات.',
  'common.catalog': 'الكتالوج',
  'common.all_products': 'كل المنتجات',
  'common.items_listed': 'عنصر مدرج',
  'common.no_products_found': 'لم يتم العثور على منتجات',
  'common.try_selecting_category': 'حاول تحديد فئة أخرى أو عرض كل المنتجات.',
  'common.sincerely': 'مع خالص التحيات،',
  'common.get_in_touch': 'تواصل معنا',
};

const TRANSLATION_CACHE_KEY = 'htg_ar_translation_cache_v2';
const ARABIC_TEXT_PATTERN = /[\u0600-\u06FF]/;
const ARABIC_MOJIBAKE_PATTERN = /[ØÙ][\s\S]*|[ŠŒŽšœžŸ€‚ƒ„…†‡ˆ‰‹‘’“”•–—˜™›]/;
const ARABIC_MOJIBAKE_REPAIR_PATTERN = /[\u00d8\u00d9][\s\S]*|[\u00c3\u00c5\u00e2\u00c6\u00cb][\s\S]*|[\u201a-\u2026\u2030-\u203a\u20ac\u2122]/;
const EMAIL_TEXT_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const URL_TEXT_PATTERN = /\b(?:https?:\/\/|www\.)\S+/gi;
const DOMAIN_TEXT_PATTERN = /\b[A-Z0-9-]+(?:\.[A-Z0-9-]+)+\b/gi;
const PROTECTED_INLINE_TEXT_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}|\b(?:https?:\/\/|www\.)\S+/i;
const TECHNICAL_REMAINDER_PATTERN = /[\s\d+()[\].,:;|/@#%&_=<>{}"'`~^*\\/-]+/g;
const TRANSLATION_BATCH_SIZE = 80;
const AUTO_TRANSLATION_ENABLED = process.env.REACT_APP_ENABLE_AUTO_TRANSLATION !== 'false';

const WINDOWS_1252_REVERSE_MAP: Record<number, number> = {
  0x20ac: 0x80,
  0x201a: 0x82,
  0x0192: 0x83,
  0x201e: 0x84,
  0x2026: 0x85,
  0x2020: 0x86,
  0x2021: 0x87,
  0x02c6: 0x88,
  0x2030: 0x89,
  0x0160: 0x8a,
  0x2039: 0x8b,
  0x0152: 0x8c,
  0x017d: 0x8e,
  0x2018: 0x91,
  0x2019: 0x92,
  0x201c: 0x93,
  0x201d: 0x94,
  0x2022: 0x95,
  0x2013: 0x96,
  0x2014: 0x97,
  0x02dc: 0x98,
  0x2122: 0x99,
  0x0161: 0x9a,
  0x203a: 0x9b,
  0x0153: 0x9c,
  0x017e: 0x9e,
  0x0178: 0x9f,
};

const isPotentialArabicMojibake = (text: string) => {
  return ARABIC_MOJIBAKE_PATTERN.test(text) || ARABIC_MOJIBAKE_REPAIR_PATTERN.test(text);
};

const repairMojibakeArabic = (text?: string) => {
  if (!text || ARABIC_TEXT_PATTERN.test(text) || !isPotentialArabicMojibake(text)) {
    return text || '';
  }

  try {
    const bytes = Uint8Array.from(Array.from(text).map((char) => {
      const codePoint = char.codePointAt(0) || 0;
      return codePoint <= 0xff ? codePoint : (WINDOWS_1252_REVERSE_MAP[codePoint] || 0x3f);
    }));
    const decoded = new TextDecoder('utf-8').decode(bytes);
    return ARABIC_TEXT_PATTERN.test(decoded) ? decoded : text;
  } catch {
    return text;
  }
};

const readTranslationCache = (): TranslationCache => {
  try {
    return JSON.parse(localStorage.getItem(TRANSLATION_CACHE_KEY) || '{}') as TranslationCache;
  } catch {
    return {};
  }
};

const hasTranslatableLetters = (text: string) => /[A-Za-z]/.test(text);

const isTechnicalOnlyText = (text: string) => {
  const textWithoutTechnicalTokens = text
    .replace(EMAIL_TEXT_PATTERN, '')
    .replace(URL_TEXT_PATTERN, '')
    .replace(DOMAIN_TEXT_PATTERN, '')
    .replace(TECHNICAL_REMAINDER_PATTERN, '');

  return !hasTranslatableLetters(textWithoutTechnicalTokens);
};

const isUsableArabicTranslation = (sourceText: string, translatedText?: string) => {
  return Boolean(
    translatedText &&
    translatedText !== sourceText &&
    ARABIC_TEXT_PATTERN.test(translatedText)
  );
};

const normalizeTranslatableText = (text: string) => {
  const sourceText = text.trim();
  if (
    !sourceText ||
    ARABIC_TEXT_PATTERN.test(sourceText) ||
    !hasTranslatableLetters(sourceText) ||
    PROTECTED_INLINE_TEXT_PATTERN.test(sourceText) ||
    isTechnicalOnlyText(sourceText)
  ) {
    return '';
  }
  return sourceText;
};

const fetchArabicTranslations = async (texts: string[]) => {
  const translations: TranslationCache = {};
  if (!AUTO_TRANSLATION_ENABLED) return translations;

  const uniqueTexts = Array.from(new Set(texts.map(normalizeTranslatableText).filter(Boolean)));
  if (uniqueTexts.length === 0) return translations;

  for (let index = 0; index < uniqueTexts.length; index += TRANSLATION_BATCH_SIZE) {
    const batch = uniqueTexts.slice(index, index + TRANSLATION_BATCH_SIZE);
    Object.assign(translations, await translateManyToArabic(batch));
  }

  return translations;
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<Language>(() => {
    return (localStorage.getItem('language') as Language) || 'en';
  });
  const [translationCache, setTranslationCache] = useState<TranslationCache>(readTranslationCache);

  const cacheRef = useRef<TranslationCache>(translationCache);
  const missingTextsRef = useRef<Set<string>>(new Set());
  const pendingTextsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';

    if (language === 'ar') {
      document.body.classList.add('rtl');
    } else {
      document.body.classList.remove('rtl');
    }
  }, [language]);

  useEffect(() => {
    cacheRef.current = translationCache;
    localStorage.setItem(TRANSLATION_CACHE_KEY, JSON.stringify(translationCache));
  }, [translationCache]);

  useEffect(() => {
    if (language !== 'ar' || missingTextsRef.current.size === 0) return;

    const texts = Array.from(missingTextsRef.current).filter((text) => {
      return !cacheRef.current[text] && !pendingTextsRef.current.has(text);
    });
    missingTextsRef.current.clear();

    if (texts.length === 0) return;
    texts.forEach((text) => pendingTextsRef.current.add(text));

    const translateMissingTexts = async () => {
      try {
        const translatedMap = await fetchArabicTranslations(texts);

        setTranslationCache((prev) => {
          const next = { ...prev };
          let changed = false;

          texts.forEach((text) => {
            const translated = translatedMap[text];
            if (isUsableArabicTranslation(text, translated)) {
              next[text] = translated;
              changed = true;
            }
          });

          return changed ? next : prev;
        });
      } catch (error) {
        console.error('Arabic translation error:', error);
      } finally {
        texts.forEach((text) => pendingTextsRef.current.delete(text));
      }
    };

    translateMissingTexts();
  });

  useEffect(() => {
    if (language !== 'ar' || typeof document === 'undefined') return;

    const textNodes = new Map<Text, string>();
    const attributeMutations: AttributeMutation[] = [];
    const waitingTextNodes = new Map<string, Set<Text>>();
    const waitingAttributes = new Map<string, Set<AttributeMutation>>();
    const queuedTexts = new Set<string>();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let disposed = false;

    const blockedTextTags = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'SVG', 'CANVAS', 'IFRAME', 'TEXTAREA', 'INPUT', 'SELECT', 'OPTION']);
    const blockedAttributeTags = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'SVG', 'CANVAS', 'IFRAME']);
    const translatableAttributes = ['placeholder', 'title', 'aria-label', 'alt'];
    const noAutoTranslateSelector = '[data-no-auto-translate="true"], a[href^="mailto:"], a[href^="tel:"]';

    const skipTextParent = (element: Element | null) => {
      return !element || blockedTextTags.has(element.tagName) || Boolean(element.closest(noAutoTranslateSelector));
    };

    const skipAttributeElement = (element: Element) => {
      return blockedAttributeTags.has(element.tagName) || Boolean(element.closest(noAutoTranslateSelector));
    };

    const applyTranslation = (sourceText: string, translated: string) => {
      const nodes = waitingTextNodes.get(sourceText);
      nodes?.forEach((node) => {
        if (node.isConnected) {
          node.nodeValue = (node.nodeValue || sourceText).replace(sourceText, translated);
        }
      });
      waitingTextNodes.delete(sourceText);

      const attributes = waitingAttributes.get(sourceText);
      attributes?.forEach(({ element, attr }) => {
        if (element.isConnected) {
          element.setAttribute(attr, (element.getAttribute(attr) || sourceText).replace(sourceText, translated));
        }
      });
      waitingAttributes.delete(sourceText);
    };

    const flushQueue = async () => {
      if (disposed || queuedTexts.size === 0) return;
      const texts = Array.from(queuedTexts);
      queuedTexts.clear();

      try {
        const translatedMap = await fetchArabicTranslations(texts);

        if (disposed) return;

        setTranslationCache((prev) => {
          const next = { ...prev };
          let changed = false;

          texts.forEach((sourceText) => {
            const translated = translatedMap[sourceText];
            if (isUsableArabicTranslation(sourceText, translated)) {
              next[sourceText] = translated;
              changed = true;
              applyTranslation(sourceText, translated);
            }
          });

          return changed ? next : prev;
        });
      } catch (error) {
        console.error('Arabic DOM translation error:', error);
      }
    };

    const requestTranslation = (sourceText: string) => {
      const cached = cacheRef.current[sourceText];
      if (isUsableArabicTranslation(sourceText, cached)) {
        applyTranslation(sourceText, cached);
        return;
      }

      queuedTexts.add(sourceText);
      if (timer) clearTimeout(timer);
      timer = setTimeout(flushQueue, 60);
    };

    const translateTextNode = (node: Text) => {
      if (skipTextParent(node.parentElement)) return;

      const original = node.nodeValue || '';
      const sourceText = normalizeTranslatableText(original);
      if (!sourceText) return;

      const cached = cacheRef.current[sourceText];
      if (isUsableArabicTranslation(sourceText, cached)) {
        if (!textNodes.has(node)) textNodes.set(node, original);
        node.nodeValue = original.replace(sourceText, cached);
        return;
      }

      if (!textNodes.has(node)) textNodes.set(node, original);
      if (!waitingTextNodes.has(sourceText)) waitingTextNodes.set(sourceText, new Set());
      waitingTextNodes.get(sourceText)?.add(node);
      requestTranslation(sourceText);
    };

    const translateAttributes = (element: Element) => {
      if (skipAttributeElement(element)) return;

      translatableAttributes.forEach((attr) => {
        const original = element.getAttribute(attr);
        if (!original) return;

        const sourceText = normalizeTranslatableText(original);
        if (!sourceText) return;

        const cached = cacheRef.current[sourceText];
        attributeMutations.push({ element, attr, original });

        if (isUsableArabicTranslation(sourceText, cached)) {
          element.setAttribute(attr, original.replace(sourceText, cached));
          return;
        }

        const mutation = { element, attr, original };
        if (!waitingAttributes.has(sourceText)) waitingAttributes.set(sourceText, new Set());
        waitingAttributes.get(sourceText)?.add(mutation);
        requestTranslation(sourceText);
      });
    };

    const scanNode = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        translateTextNode(node as Text);
        return;
      }

      if (node.nodeType !== Node.ELEMENT_NODE) return;

      const element = node as Element;
      translateAttributes(element);

      const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
        acceptNode: (textNode) => {
          return skipTextParent(textNode.parentElement) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
        },
      });

      const nodesToTranslate: Text[] = [];
      let current = walker.nextNode();
      while (current) {
        nodesToTranslate.push(current as Text);
        current = walker.nextNode();
      }
      nodesToTranslate.forEach(translateTextNode);

      element.querySelectorAll(translatableAttributes.map((attr) => `[${attr}]`).join(',')).forEach(translateAttributes);
    };

    scanNode(document.body);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'characterData') {
          scanNode(mutation.target);
        }

        if (mutation.type === 'attributes' && mutation.target.nodeType === Node.ELEMENT_NODE) {
          translateAttributes(mutation.target as Element);
        }

        mutation.addedNodes.forEach(scanNode);
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: translatableAttributes,
    });

    return () => {
      disposed = true;
      if (timer) clearTimeout(timer);
      observer.disconnect();
      textNodes.forEach((original, node) => {
        if (node.isConnected) node.nodeValue = original;
      });
      attributeMutations.forEach(({ element, attr, original }) => {
        if (element.isConnected) element.setAttribute(attr, original);
      });
    };
  }, [language]);

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === 'en' ? 'ar' : 'en'));
  };

  const getArabicText = (text: string, arText?: string) => {
    const repairedArText = repairMojibakeArabic(arText);
    if (repairedArText && ARABIC_TEXT_PATTERN.test(repairedArText)) return repairedArText;
    if (!text) return '';
    if (ARABIC_TEXT_PATTERN.test(text)) return text;

    const sourceText = normalizeTranslatableText(text);
    if (!sourceText) return text;

    const cached = cacheRef.current[sourceText];
    if (isUsableArabicTranslation(sourceText, cached)) return cached;

    if (!pendingTextsRef.current.has(sourceText)) {
      missingTextsRef.current.add(sourceText);
    }

    return text;
  };

  const t = (key: string) => {
    const text = englishTranslations[key] || key;
    return language === 'ar' ? (repairMojibakeArabic(arabicTranslations[key]) || getArabicText(text)) : text;
  };

  const t_api = (text: string, arText?: string) => {
    return language === 'ar' ? getArabicText(text, arText) : text;
  };

  const isArabic = language === 'ar';

  return (
    <LanguageContext.Provider value={{ language, isArabic, toggleLanguage, t, t_api }}>
      <div className={language === 'ar' ? 'font-arabic rtl' : ''} dir={language === 'ar' ? 'rtl' : 'ltr'}>
        {children}
      </div>
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
